function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
function _objectWithoutProperties(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useMemo, forwardRef, useCallback } from "react";
import { ItemMemberLevel, Post_ItemOrCountsUpdatedDocument } from "sprout-graphql";
import { BreadcrumbsPseudo } from "sprout-ui-dom/dist/Breadcrumbs";
import styled, { css } from "styled-components";
import { getItemUrl, isNotNull } from "sprout-lib";
import { themeGet, media } from "sprout-ui-theme";
import { sortLabels } from "@sprout/client-lib/dist/labels_next";
import { Flex, Box } from "sprout-ui-primitives";
import useCurrentUser from "@sprout/client-graphql/dist/useCurrentUser";
import { useMergeRefs } from "sprout-hooks";
import FeatureFlaggedSubscription from "@sprout/client-lib/dist/FeatureFlaggedSubscription";
import RenderIfChildExists from "../RenderIfChildExists";
import { Card, CardLink, CardContent } from "../Card";
import { UrlDisplay } from "../UrlDisplay";
import PostAvatar from "./Post_Avatar";
import PostContent from "./PostContent";
import PostLabel from "./PostLabel";
import PostIndicators from "./PostIndicators";
import PostMarkers from "./PostMarkers";
import PostCompleted from "./PostCompleted";
import PostMultiselect from "./PostMultiselect";
import PostActions from "./PostActions";
const PostWrapper = styled(Card)`
  ${media.up("medium")} {
    padding: 10px 0;
  }

  ${media.up("medium", "pointer")} {
    :hover {
      .PostActions {
        display: inline-flex;
      }

      label {
        opacity: 1;
      }
    }
  }
`;
const PostLabelsMobileWrapper = styled(Box)`
  ${media.up("medium", "pointer")} {
    display: none;
  }
`;
const PostBreadcrumbsPseudo = styled(BreadcrumbsPseudo)`
  font-size: 13px;
  font-weight: ${themeGet("fontWeights.bold")};
  line-height: 16px;
  color: ${themeGet("colors.blue.500")};
`;
const UrlDisplayWrapper = styled(Box)`
  ${({ $mt  })=>$mt ? css`
          margin-top: 16px;
        ` : css`
          ${media.down("medium")} {
            margin-top: 24px;
          }
        `}
`;
const PostForwardedRef = /*#__PURE__*/ forwardRef(function Post(props, ref) {
    const { post , dragProvided , isDragging , showUser: showUserProp = false , showTeam , showPseudoBreadcrumbs: showPseudoBreadcrumbsProp , showMarkers =true , showCompleted =true , showWhen =true , compactUrlDisplay =true , navigationItemHelpers , className , onActionHandled  } = props, rest = _objectWithoutProperties(props, [
        "post",
        "dragProvided",
        "isDragging",
        "showUser",
        "showTeam",
        "showPseudoBreadcrumbs",
        "showMarkers",
        "showCompleted",
        "showWhen",
        "compactUrlDisplay",
        "navigationItemHelpers",
        "className",
        "onActionHandled"
    ]);
    const { id , owner , rootTopicOrSelf , parentTopic , description , url , note , appliedLabels , completed , checklistItemTotalCount , checklistItemCompletedCount , commentCount , assignments , userPreferences , linkImage , linkTitle , linkDescription , linkLogo , websiteName , deletedAt , when , whenReminder , deadline , currentUserHasStarred ,  } = post;
    const currentUser = useCurrentUser();
    const subscriptionVariables = useMemo(()=>{
        return {
            postId: id
        };
    }, [
        id
    ]);
    const { owner: rootUser , memberLevel  } = rootTopicOrSelf || {};
    //
    const isCurrentUser = (currentUser === null || currentUser === void 0 ? void 0 : currentUser.id) === (owner === null || owner === void 0 ? void 0 : owner.id);
    const disabled = !(memberLevel && [
        ItemMemberLevel.Owner,
        ItemMemberLevel.Admin
    ].includes(memberLevel) || memberLevel === ItemMemberLevel.Contributor && isCurrentUser);
    // Remove `hidden` style label since they are not visible on `Post`
    const labels = useMemo(()=>{
        return sortLabels((appliedLabels || []).filter(isNotNull));
    }, [
        appliedLabels
    ]);
    //
    const checklistsCount = useMemo(()=>{
        return {
            total: checklistItemTotalCount || 0,
            completed: checklistItemCompletedCount || 0
        };
    }, [
        checklistItemCompletedCount,
        checklistItemTotalCount
    ]);
    //
    const assignees = useMemo(()=>{
        return assignments.map(({ assignee  })=>assignee).filter(isNotNull);
    }, [
        assignments
    ]);
    //
    const emptyPostContent = !url && !note && labels.length === 0;
    const showPostContent = !!description || emptyPostContent || !!note || labels.length > 0 || showWhen && !!when || !!whenReminder || !!currentUserHasStarred;
    const showPostIndicators = checklistsCount.total > 0 || commentCount > 0 || assignees.length > 0 || deadline !== null;
    const rootURL = useMemo(()=>getItemUrl(rootTopicOrSelf, rootUser), [
        rootTopicOrSelf,
        rootUser
    ]);
    const mergedRefs = useMergeRefs(dragProvided === null || dragProvided === void 0 ? void 0 : dragProvided.innerRef, ref);
    const { focusWhen  } = userPreferences || {};
    // list navigation
    const { isSelectable , isMultiselectActive , toggleMultiselect , isSelected , isActive ,  } = navigationItemHelpers || {};
    // Fancy trick to make the whole card selectable when multiselect is active
    const handleCardClick = useCallback((event)=>{
        if (isMultiselectActive && isSelectable && toggleMultiselect) {
            event.preventDefault();
            toggleMultiselect();
        }
    }, [
        isMultiselectActive,
        isSelectable,
        toggleMultiselect
    ]);
    const showUser = showUserProp && owner;
    const showPseudoBreadcrumbs = showPseudoBreadcrumbsProp && parentTopic && rootTopicOrSelf && rootUser;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(FeatureFlaggedSubscription, {
                subscription: Post_ItemOrCountsUpdatedDocument,
                variables: subscriptionVariables
            }),
            /*#__PURE__*/ _jsxs(PostWrapper, _objectSpread({}, dragProvided && _objectSpread({}, dragProvided.draggableProps, dragProvided.dragHandleProps), rest, {
                ref: mergedRefs,
                isDragging: isDragging,
                isActive: isActive,
                isSelected: isSelected,
                className: className,
                onClick: handleCardClick,
                "data-id": id,
                children: [
                    !isMultiselectActive && /*#__PURE__*/ _jsx(CardLink, {
                        to: getItemUrl(post)
                    }),
                    showMarkers && focusWhen && /*#__PURE__*/ _jsx(PostMarkers, {
                        focusWhen: focusWhen
                    }),
                    /*#__PURE__*/ _jsxs(CardContent, {
                        children: [
                            !disabled && !isMultiselectActive && /*#__PURE__*/ _jsx(PostActions, {
                                onActionHandled: onActionHandled,
                                post: post
                            }),
                            /*#__PURE__*/ _jsxs(Flex, {
                                children: [
                                    navigationItemHelpers && /*#__PURE__*/ _jsx(PostMultiselect, {
                                        disabled: disabled,
                                        navigationItemHelpers: navigationItemHelpers
                                    }),
                                    showCompleted && /*#__PURE__*/ _jsx(PostCompleted, {
                                        post: post,
                                        disabled: disabled || // Can't update `completed` of trash post
                                        deletedAt !== null
                                    }),
                                    /*#__PURE__*/ _jsxs(Box, {
                                        minWidth: "0",
                                        flex: "auto",
                                        opacity: showCompleted && completed ? 0.42 : undefined,
                                        children: [
                                            /*#__PURE__*/ _jsxs(RenderIfChildExists, {
                                                component: /*#__PURE__*/ _jsx(Flex, {}),
                                                children: [
                                                    showPostContent && /*#__PURE__*/ _jsx(PostContent, {
                                                        currentUserHasStarred: currentUserHasStarred,
                                                        when: showWhen ? when : null,
                                                        whenReminder: whenReminder,
                                                        description: description ? description.trim() : emptyPostContent ? "No description" : undefined,
                                                        completed: showCompleted && completed,
                                                        note: !!note,
                                                        labels: labels,
                                                        rootURL: rootURL
                                                    }),
                                                    showPostIndicators && /*#__PURE__*/ _jsx(PostIndicators, {
                                                        variant: "inline",
                                                        assignees: assignees,
                                                        checklistsCount: checklistsCount,
                                                        commentCount: commentCount,
                                                        deadline: deadline
                                                    })
                                                ]
                                            }),
                                            url && /*#__PURE__*/ _jsx(UrlDisplayWrapper, {
                                                $mt: showPostContent || showPostIndicators,
                                                children: /*#__PURE__*/ _jsx(UrlDisplay, {
                                                    url: url,
                                                    title: linkTitle,
                                                    image: linkImage,
                                                    logo: linkLogo,
                                                    description: linkDescription,
                                                    websiteName: websiteName,
                                                    compact: compactUrlDisplay
                                                })
                                            }),
                                            labels.length > 0 && /*#__PURE__*/ _jsx(PostLabelsMobileWrapper, {
                                                mt: 3,
                                                children: labels.map((label)=>{
                                                    return /*#__PURE__*/ _jsx(PostLabel, _objectSpread({}, label), `${label.style}-${label.text}`);
                                                })
                                            }),
                                            showPostIndicators && /*#__PURE__*/ _jsx(PostIndicators, {
                                                variant: "solo",
                                                assignees: assignees,
                                                checklistsCount: checklistsCount,
                                                commentCount: commentCount,
                                                deadline: deadline
                                            }),
                                            /*#__PURE__*/ _jsxs(RenderIfChildExists, {
                                                component: /*#__PURE__*/ _jsx(Flex, {
                                                    alignItems: "center",
                                                    mt: 3
                                                }),
                                                children: [
                                                    showUser && /*#__PURE__*/ _jsx(PostAvatar, {
                                                        owner: owner
                                                    }),
                                                    showPseudoBreadcrumbs && /*#__PURE__*/ _jsx(PostBreadcrumbsPseudo, {
                                                        showTeam: showTeam,
                                                        owner: rootUser,
                                                        parentTopic: parentTopic,
                                                        rootTopic: rootTopicOrSelf,
                                                        renderAsLink: true
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }))
        ]
    });
});
export default /*#__PURE__*/ memo(PostForwardedRef);
